import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/useAuth";

export type AuthMode = "login" | "register" | "reset";

export interface UseLoginAuthResult {
  mode: AuthMode;
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  signupSource: string;
  error: string | null;
  isSubmitting: boolean;
  resetSent: boolean;
  isConfigured: boolean;
  user: ReturnType<typeof useAuth>["user"];
  loading: boolean;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setName: (value: string) => void;
  setSignupSource: (value: string) => void;
  switchMode: (newMode: AuthMode) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleGoogleSignIn: () => Promise<void>;
}

// Strips Firebase's "Firebase: ... (auth/code)." envelope and trailing
// punctuation. Returns the cleaned message, or null if nothing
// meaningful remains (e.g. "Firebase: Error (auth/internal-error)."
// collapses to nothing) so the caller can fall back to an i18n string.
function cleanFirebaseError(message: string): string | null {
  const cleaned = message
    .replace(/^Firebase:\s*/, "")
    .replace(/\s*\(auth\/[^)]+\)\.?\s*$/, "")
    .replace(/\.$/, "")
    .trim();
  if (!cleaned || cleaned.toLowerCase() === "error") return null;
  return cleaned;
}

// Firebase answers `auth/invalid-credential` for BOTH a wrong password and an
// unknown account (email-enumeration protection). The legacy codes map to the
// same key so the UI never reveals whether an account exists.
const FIREBASE_CODE_TO_ERROR_KEY: Record<string, string> = {
  "auth/invalid-credential": "auth:errors.invalidCredentials",
  "auth/wrong-password": "auth:errors.invalidCredentials",
  "auth/user-not-found": "auth:errors.invalidCredentials",
  "auth/too-many-requests": "auth:errors.tooManyRequests",
  "auth/network-request-failed": "auth:errors.networkError",
  "auth/user-disabled": "auth:errors.userDisabled",
};

function firebaseErrorCode(err: unknown): string | null {
  if (typeof err !== "object" || err === null || !("code" in err)) return null;
  return typeof err.code === "string" ? err.code : null;
}

export function useLoginAuth(): UseLoginAuthResult {
  const { t } = useTranslation(["auth"]);
  const { signIn, signUp, signInWithGoogle, resetPassword, isConfigured, user, loading } =
    useAuth();
  const [mode, setMode] = useState<AuthMode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  // "Skąd o nas wiesz?" (GTM plan F2). Empty means "not answered": the field
  // is optional, and an unanswered source must stay unknown rather than
  // defaulting to a value we would later report as fact.
  const [signupSource, setSignupSource] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const resolveAuthError = (err: unknown, fallbackKey: string): string => {
    const code = firebaseErrorCode(err);
    const mappedKey = code ? FIREBASE_CODE_TO_ERROR_KEY[code] : undefined;
    if (mappedKey) return t(mappedKey);
    const raw = err instanceof Error ? err.message : null;
    return (raw && cleanFirebaseError(raw)) ?? t(fallbackKey);
  };

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setResetSent(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (mode === "login") {
        await signIn(email, password);
      } else if (mode === "register") {
        if (password !== confirmPassword) throw new Error(t("auth:errors.passwordsDoNotMatch"));
        if (password.length < 6) throw new Error(t("auth:errors.passwordTooShort"));
        await signUp(email, password, name || undefined, signupSource || undefined);
      } else {
        await resetPassword(email);
        setResetSent(true);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(resolveAuthError(err, "auth:errors.authFailed"));
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(resolveAuthError(err, "auth:errors.googleSignInFailed"));
      setIsSubmitting(false);
    }
  };

  return {
    mode,
    email,
    password,
    confirmPassword,
    name,
    signupSource,
    error,
    isSubmitting,
    resetSent,
    isConfigured,
    user,
    loading,
    setEmail,
    setPassword,
    setConfirmPassword,
    setName,
    setSignupSource,
    switchMode,
    handleSubmit,
    handleGoogleSignIn,
  };
}
